// library imports
import { Segmented, Card, Flex, Tooltip, Skeleton } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useState, useEffect, useContext } from "react";
// style imports
import "../../styles/Dashboard/SummaryCard.css";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// component imports
import _SingleContent from "./_SingleContent";
import _SingleContentMd from "./_SingleContentMd";
// config imports
import { MICROGRID_SITE_IDS } from "../../configs/configs";

function SummaryCard() {
  const [fetchWrapper] = useFetch();
  const { siteSelected, notificationApi, hasGensetSelected } =
    useContext(IndexContext);

  const [loading, setLoading] = useState(false);

  const summaryDateTypeOptions = [
    { label: "Day", value: "day" },
    { label: "Month", value: "month" },
    { label: "Year", value: "year" },
  ];

  const [summaryDateSelection, setSummaryDateSelection] = useState(
    summaryDateTypeOptions[1].value,
  );

  // system production
  const [pvSystemProduction, setPvSystemProduction] = useState(0);
  const [pvSelfConsumption, setPvSelfConsumption] = useState(0); // this one is pv system supplied for load consumption as well
  const [tnbGridExport, setTnbGridExport] = useState(0);

  // load consumption
  const [loadConsumption, setLoadConsumption] = useState(0);
  const [tnbGridSupplied, setTnbGridSupplied] = useState(0);
  const [gensetSupplied, setGensetSupplied] = useState(0);

  // maximum demand
  const [loadMD, setLoadMD] = useState(0);
  const [loadMDDateTime, setLoadMDDateTime] = useState("-");
  const [MDReductionAI, setMDReductionAI] = useState(0);
  const [tnbMD, setTnbMD] = useState(0);
  const [tnbMDDateTime, setTnbMDDateTime] = useState("-");

  function getDaySummary() {
    setLoading(true);

    fetchWrapper({
      endpoint_url: "dashboard/getDaySummary",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setPvSelfConsumption(response.data.pv_self_consumption);
        setTnbGridExport(response.data.tnb_grid_export);
        setTnbGridSupplied(response.data.tnb_grid_supplied);
        setGensetSupplied(response.data.genset_supplied);
        setTnbMD(response.data.tnb_day_MD);
        setLoadMD(response.data.load_MD);
        setLoadMDDateTime(response.data.load_MD_datetime);
        setMDReductionAI(response.data.MD_reduction_AI);
        setTnbMDDateTime(response.data.tnb_day_MD_datetime);
        setPvSystemProduction(response.data.pv_system_production);
        setLoadConsumption(response.data.load_consumption);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get summary of the day.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoading(false);
      },
    });
  }

  function getMonthSummary() {
    setLoading(true);

    fetchWrapper({
      endpoint_url: "dashboard/getMonthSummary",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setPvSelfConsumption(response.data.pv_self_consumption);
        setTnbGridExport(response.data.tnb_grid_export);
        setTnbGridSupplied(response.data.tnb_grid_supplied);
        setGensetSupplied(response.data.genset_supplied);
        setTnbMD(response.data.tnb_month_MD);
        setLoadMD(response.data.load_MD);
        setLoadMDDateTime(response.data.load_MD_datetime);
        setMDReductionAI(response.data.MD_reduction_AI);
        setTnbMDDateTime(response.data.tnb_month_MD_datetime);
        setPvSystemProduction(response.data.pv_system_production);
        setLoadConsumption(response.data.load_consumption);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get summary of the month.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoading(false);
      },
    });
  }

  function getYearSummary() {
    setLoading(true);

    fetchWrapper({
      endpoint_url: "dashboard/getYearSummary",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setPvSelfConsumption(response.data.pv_self_consumption);
        setTnbGridExport(response.data.tnb_grid_export);
        setTnbGridSupplied(response.data.tnb_grid_supplied);
        setGensetSupplied(response.data.genset_supplied);
        setTnbMD(response.data.tnb_year_MD);
        setLoadMD(response.data.load_MD);
        setLoadMDDateTime(response.data.load_MD_datetime);
        setMDReductionAI(response.data.MD_reduction_AI);
        setTnbMDDateTime(response.data.tnb_year_MD_datetime);
        setPvSystemProduction(response.data.pv_system_production);
        setLoadConsumption(response.data.load_consumption);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get summary of the year.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoading(false);
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      if (summaryDateSelection === "day") {
        getDaySummary();

        const timer = setInterval(
          () => {
            getDaySummary();
          },
          30 * 60 * 1000,
        );

        return () => {
          clearInterval(timer);
        };
      } else if (summaryDateSelection === "month") {
        getMonthSummary();

        const timer = setInterval(
          () => {
            getMonthSummary();
          },
          30 * 60 * 1000,
        );

        return () => {
          clearInterval(timer);
        };
      } else if (summaryDateSelection === "year") {
        getYearSummary();

        const timer = setInterval(
          () => {
            getYearSummary();
          },
          30 * 60 * 1000,
        );

        return () => {
          clearInterval(timer);
        };
      }
    }
  }, [summaryDateSelection, siteSelected]);

  return (
    <Card
      size="small"
      className="w-100"
      title={
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={8}>
            <span>Summary</span>
            <span>
              <Tooltip title={"The values are updated every 30 minutes."}>
                <InfoCircleOutlined />
              </Tooltip>
            </span>
          </Flex>

          <Segmented
            options={summaryDateTypeOptions}
            onChange={(value) => {
              setSummaryDateSelection(value);
            }}
            value={summaryDateSelection}
            disabled={loading}
          />
        </Flex>
      }
      styles={{
        body: {
          // min height = 100vh - nav menu header - site selection - system info card height - summary card header - gap between system info card and summary card - summary card padding
          minHeight: "calc(100vh - 58px - 50px - 323px - 38px - 4px - 24px)",
          height: loading ? "100%" : "auto",
          display: loading ? "flex" : "block",
          justifyContent: "center",
          alignItems: "center",
        },
      }}
    >
      {loading === false ? (
        <div className="summary-content-container">
          <_SingleContent
            title={"Load Consumption"}
            total={loadConsumption}
            var1label={"PV System"}
            var2label={"Grid Supplied"}
            var3label={hasGensetSelected ? "Genset Supplied" : "-"}
            var1value={pvSelfConsumption}
            var2value={tnbGridSupplied}
            var3value={hasGensetSelected ? gensetSupplied : "-"}
            unit={"kWh"}
          />
          <_SingleContent
            title={"System production (AC)"}
            total={pvSystemProduction}
            var1label={"Self-Consumption"}
            var2label={"Grid Export"}
            var1value={pvSelfConsumption}
            var2value={tnbGridExport}
            unit={"kWh"}
          />
          <_SingleContentMd
            title={"Max Demand"}
            total={loadMD}
            var1label={"MD Reduction by AIE-DR"}
            var2label={
              MICROGRID_SITE_IDS.includes(siteSelected)
                ? "Genset Supplied"
                : "Grid Supplied"
            }
            var1value={MDReductionAI}
            var2value={tnbMD}
            unit={"kW"}
            loadMDDateTime={loadMDDateTime}
            tnbMDDateTime={tnbMDDateTime}
          />
        </div>
      ) : (
        <Skeleton
          active={true}
          style={{ padding: "1rem", maxWidth: "70%", margin: "auto" }}
          paragraph={{ rows: 9 }}
        />
      )}
    </Card>
  );
}

export default SummaryCard;
